import { useEffect, useMemo, useState } from "react";
import useAbort from "../../../../common/hooks/use-abort";
import useIsComponentMounted from "../../../../common/hooks/use-is-component-mounted";
import useOrderOptimizerList from "../../../../common/services/order-optimizer/list/use-order-optimizer-list";
import CardComponent from "../../../../common/components/card/card.component";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import ListingPaginationComponent from "../../../../common/components/listing/pagination/listing-pagination.component";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import usePagination from "../../../../common/hooks/use-pagination";
import OrderOptimizerListParams from "../../../../common/services/order-optimizer/list/order-optimizer-list-params";
import { useAppContext } from "../../../../context/app.context";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import orderOptimizerTranslationsHelper from "../../../../languages/order-optimizer-translations.helper";
import orderOptimizerBreadcrumbsHelper from "../../common/breadcrumbs/order-optimizer-breadcrumbs.helper";
import orderOptimizerCargoListingItemFactory from "./common/order-optimizer-cargo-listing-item.factory";
import orderOptimizerCargoListingLoadParamsFactory from "./common/order-optimizer-cargo-listing-load-params.factory";
import OrderOptimizerCargoListingFilter from "./common/types/order-optimizer-cargo-listing-filter";
import OrderOptimizerCargoListingFilterType from "./common/types/order-optimizer-cargo-listing-filter-type";
import { OrderOptimizerCargoListingItem } from "./common/types/order-optimizer-cargo-listing-item";
import OrderOptimizerCargoListingFiltersComponent from "./filters/order-optimizer-cargo-listing-filters.component";
import useOrderOptimizerCargoListingFiltersDao from "./filters/use-order-optimizer-cargo-listing-filters.dao";
import OrderOptimizerCargoListingTableComponent from "./table/order-optimizer-cargo-listing-table.component";

const OrderOptimizerCargoListingComponent = () => {
  const isComponentMounted = useIsComponentMounted();

  const orderOptimizerList = useOrderOptimizerList();
  const orderOptimizerListAbort = useAbort();

  const filtersDao = useOrderOptimizerCargoListingFiltersDao();

  const [filters, setFilters] = useState<OrderOptimizerCargoListingFilter[]>(
    () => filtersDao.getFilters(),
  );

  const selectedHumanId = useMemo(() => {
    const filterValue = filters.find(
      (filter) =>
        filter.type === OrderOptimizerCargoListingFilterType.ORDER_HUMAN_ID,
    )?.value as string | undefined;

    return Number.parseInt(filterValue ?? "");
  }, [filters]);

  const loadListingItems = () => {
    const params: OrderOptimizerListParams =
      orderOptimizerCargoListingLoadParamsFactory.create(
        filters,
        page,
        pageSize,
      );

    orderOptimizerList.load(params, orderOptimizerListAbort.signal);
  };

  const { page, pageSize, setPage, setPageSize } = usePagination({
    totalResults: orderOptimizerList.data.totalCount,
    defaultPageSize: filtersDao.getPageSize() ?? 10, // limit set to 10 (default 100)
    defaultPage: filtersDao.getPage(),
  });

  const saveFilters = () => {
    filtersDao.saveFilters(filters, page, pageSize);
  };

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    saveFilters();
  }, [filters, page, pageSize, isComponentMounted]);

  useEffect(() => {
    loadListingItems();

    return () => orderOptimizerListAbort.revoke();
  }, [page, pageSize, filters]);

  const translations =
    orderOptimizerTranslationsHelper.getCargoListingTranslations();
  const { selectedAppLanguage, setBreadcrumbs } = useAppContext();

  useEffect(() => {
    const breadcrumbs = orderOptimizerBreadcrumbsHelper.getListingBreadcrumbs();
    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage]);

  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  useDocumentTitle(documentTitleTranslations.orderOptimizerListing);

  const listingItems: OrderOptimizerCargoListingItem[] = useMemo(() => {
    return orderOptimizerCargoListingItemFactory.createListingItems(
      orderOptimizerList.data.data,
    );
  }, [orderOptimizerList.data]);

  return (
    <>
      <HeadingComponent title={translations.header.headingLabel} />
      <OrderOptimizerCargoListingFiltersComponent
        filters={filters}
        isListingLoading={orderOptimizerList.isLoading}
        onFiltersChange={(filters) => {
          setFilters(filters);
          setPage(1);
        }}
      />
      <CardComponent>
        <OrderOptimizerCargoListingTableComponent
          listingItems={listingItems}
          selectedHumanId={selectedHumanId}
          isError={orderOptimizerList.isError}
          isLoading={orderOptimizerList.isLoading}
          applyOptimizationStatus={orderOptimizerList.applyOptimizationStatus}
        />
        <ListingPaginationComponent
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          page={page}
          pageSize={pageSize}
          totalResults={orderOptimizerList.data.totalCount}
        />
      </CardComponent>
    </>
  );
};

export default OrderOptimizerCargoListingComponent;
